import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { VOApi } from 'modules/vendorOpportunity';
import { useNotifications } from 'modules/notification';
import type { MutationHookOptions } from '@apollo/client/react/types/types';

import { bpfAPI } from '../index';
import { CloseDealDTOSchema, ReopenDealDTOSchema } from './schema';
import { CloseDealDTO, dealLifeCycleApi, ReopenDealDTO } from './index';

export function useDealLifeCycle() {
  const { showNotification } = useNotifications();

  const [onCloseDeal, { loading: isDealClosing }] = useMutation(dealLifeCycleApi.closeDeal, { onError: logError });

  const handleCloseDeal = async (
    dto: CloseDealDTO,
    fetchOptions?: (api: typeof dealLifeCycleApi) => Omit<MutationHookOptions, 'variables'>
  ) => {
    try {
      const { data: parsedData, success: parseSuccess, error: parseError } = CloseDealDTOSchema.safeParse(dto);

      if (!parseSuccess) {
        logError(parseError);
        showNotification('Bad input. Please check and try again');
        return null;
      }

      const { data, errors } = await onCloseDeal({
        variables: { input: parsedData },
        ...(typeof fetchOptions === 'function' ? fetchOptions(dealLifeCycleApi) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCloseDeal error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Deal closed successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const [onReopenDeal, { loading: isDealReopening }] = useMutation(dealLifeCycleApi.reopenDeal, { onError: logError });

  const handleReopenDeal = async (
    dto: ReopenDealDTO,
    fetchOptions?: (api: typeof dealLifeCycleApi) => Omit<MutationHookOptions, 'variables'>
  ) => {
    try {
      const { data: parsedData, success: parseSuccess, error: parseError } = ReopenDealDTOSchema.safeParse(dto);

      if (!parseSuccess) {
        logError(parseError);
        showNotification('Bad input. Please check and try again');
        return null;
      }

      const { data, errors } = await onReopenDeal({
        variables: { input: parsedData },
        ...(typeof fetchOptions === 'function' ? fetchOptions(dealLifeCycleApi) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleReopenDeal error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Deal reopened successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const [onUpdateDealClosure, { loading: isUpdatingDealClosure }] = useMutation(dealLifeCycleApi.updateDealClosure, {
    onError: logError,
  });

  const [onUpdateVendorOpportunity, { loading: isUpdatingClosedOn }] = useMutation(VOApi.update, {
    onError: logError,
  });

  const handleUpdateDealClosure = async (
    dto: { id: string; closedDateTime: string; reasonId: string },
    fetchOptions?: (api: typeof dealLifeCycleApi, bpfApi: typeof bpfAPI) => Omit<MutationHookOptions, 'variables'>
  ) => {
    try {
      const options = typeof fetchOptions === 'function' ? fetchOptions(dealLifeCycleApi, bpfAPI) : {};

      const [updateResult] = await Promise.all([
        onUpdateDealClosure({
          variables: { input: dto },
          ...options,
        }),
        onUpdateVendorOpportunity({
          variables: {
            VOId: dto.id,
            fieldsObj: { closedOn: dto.closedDateTime },
          },
        }),
      ]);

      const { data, errors } = updateResult;

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleUpdateDealClosure error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Deal Closure successfully updated.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  return {
    loading: {
      isDealClosing,
      isDealReopening,
      isUpdatingDealClosure: isUpdatingDealClosure || isUpdatingClosedOn,
    },
    actions: {
      handleCloseDeal,
      handleReopenDeal,
      handleUpdateDealClosure,
    },
  };
}
